import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Box,
  Alert,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Typography,
  Link,
  Divider
} from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import EmailSelectionDialog from './EmailSelectionDialog';
import ContactPopup from '../popups/ContactPopup';
import ExternalLinkDialog from '../common/ExternalLinkDialog';

const validationSchema = Yup.object({
  username: Yup.string().required('Username is required')
});

const LoginForm = () => {
  const { login } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cooldown, setCooldown] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailOptions, setEmailOptions] = useState([]);
  const [submittingEmailSelection, setSubmittingEmailSelection] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [externalDialogOpen, setExternalDialogOpen] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');
  const [externalTitle, setExternalTitle] = useState('');
  const [externalMessage, setExternalMessage] = useState('');

  const handleExternalLink = (url, title, message) => {
    setExternalUrl(url);
    setExternalTitle(title);
    setExternalMessage(message);
    setExternalDialogOpen(true);
  };

  const handleExternalConfirm = () => {
    window.open(externalUrl, '_blank');
    setExternalDialogOpen(false);
  };

  const formik = useFormik({
    initialValues: {
      username: ''
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setCooldown(true);
      setTimeout(() => setCooldown(false), 6000);
      
      try {
        setError(''); // Clear any previous errors
        setSuccess(''); // Clear any previous success messages
        const result = await login({ username: values.username });
        
        if (result.success) {
          if (result.requiresEmailSelection && result.emails) {
            // If multiple email options, show dialog
            setEmailOptions(result.emails);
            setEmailDialogOpen(true);
          } else {
            // If single email, show success message
            setSuccess('Sign in link has been sent to your email. Please check your inbox and click the link to sign in. Be sure to check your spam folder if you don\'t see it in your inbox.');
          }
        } else {
          setError(result.message || 'An error occurred during login');
        }
      } catch (err) {
        // Handle rate limit error specifically
        if (err.response?.status === 429) {
          setError(err.response.data.message || 'Please wait before requesting another sign in link');
        } else {
          setError(err.response?.data?.message || 'An error occurred during login');
        }
      } finally {
        setSubmitting(false);
      }
    }
  });

  const handleEmailSelect = async (selectedIndex) => {
    try {
      setSubmittingEmailSelection(true);
      setError('');
      const result = await login({
        username: formik.values.username,
        selectedEmailIndex: selectedIndex
      });
      
      setEmailDialogOpen(false);
      
      if (result.success) {
        setSuccess('Sign in link has been sent to your email. Please check your inbox and click the link to sign in.');
      } else {
        setError(result.message || 'Failed to send magic link');
      }
    } catch (error) {
      // Handle rate limit error specifically
      if (error.response?.status === 429) {
        setError(error.response.data.message || 'Please wait before requesting another sign in link');
      } else {
        setError('Failed to send magic link');
      }
    } finally {
      setSubmittingEmailSelection(false);
    }
  };

  return (
    <Box 
      component="form" 
      onSubmit={formik.handleSubmit}
      noValidate
      sx={{ 
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}
    >
      {(error || success) && (
        <Alert 
          severity={error ? "error" : "success"}
          sx={{ mb: 2 }}
        >
          {error || success}
        </Alert>
      )}

      <TextField
        fullWidth
        id="username"
        name="username"
        label="Username"
        value={formik.values.username}
        onChange={formik.handleChange}
        error={formik.touched.username && Boolean(formik.errors.username)}
        helperText={formik.touched.username && formik.errors.username}
        disabled={formik.isSubmitting}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={formik.isSubmitting || cooldown}
        sx={{ mt: 2 }}
      >
        {formik.isSubmitting ? <CircularProgress size={24} /> : 'Sign In'}
      </Button>

      <Box sx={{ 
        mt: 2, 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%'
      }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/register')}
          sx={{ 
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          Need an account?
        </Link>
        <Typography variant="body2" color="text.secondary">
          <Link 
            component="button"
            onClick={() => handleExternalLink(
              'https://totalturf.ezfacility.com/RetrieveUsername',
              'Retrieve Username',
              'Retrieve your username on the EZ Facility website. Once done, come back here and log in.'
            )}
            sx={{ 
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            Forgot Username?
          </Link>
        </Typography>
      </Box>

      <Divider sx={{ mt: 2, mb: 1 }} />

      <Typography
        variant="body1"
        color="primary"
        align="center"
        sx={{ 
          cursor: 'pointer',
          '&:hover': {
            textDecoration: 'underline'
          }
        }}
        onClick={() => setContactDialogOpen(true)}
      >
        Having issues? Contact us
      </Typography>

      <EmailSelectionDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        emails={emailOptions}
        onSelect={handleEmailSelect}
      />

      <ContactPopup
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
      />

      <ExternalLinkDialog
        open={externalDialogOpen}
        onClose={() => setExternalDialogOpen(false)}
        url={externalUrl}
        title={externalTitle}
        message={externalMessage}
        onConfirm={handleExternalConfirm}
      />
    </Box>
  );
};

export default LoginForm; 